import { CheckIcon, ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    Input,
    InputGroup,
    InputLeftElement,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Portal,
    Tag,
    Text,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { translate } from '../i18n';
import { SportType } from '../models/court/dto/court.dto';
import { useSemanticColor } from '../theme/tokens';

type SportSelectProps = {
    /** Currently selected sports. */
    value: SportType[];
    /** Called with the full next selection on every toggle. */
    onChange: (next: SportType[]) => void;
    /**
     * `inline` (default) renders a wrapping grid of toggle chips with no popover and
     * no inner scroll — the form scrolls once. `compact` renders a small trigger that
     * opens a searchable checkbox menu, for tight filter bars.
     */
    mode?: 'inline' | 'compact';
    /** Sports to offer. Defaults to every SportType, sorted alphabetically. */
    options?: SportType[];
    isInvalid?: boolean;
    /** compact only — trigger label when nothing is selected. */
    emptyLabel?: string;
    /** compact only — plural unit shown as "3 {unitLabel}" past 3 selections. */
    unitLabel?: string;
};

const ALL_SPORTS = Object.values(SportType).sort();

export default function SportSelect({
    value,
    onChange,
    mode = 'inline',
    options,
    isInvalid = false,
    emptyLabel,
    unitLabel,
}: SportSelectProps) {
    const sports = options ?? ALL_SPORTS;

    const toggle = (sport: SportType) => {
        onChange(value.includes(sport) ? value.filter(s => s !== sport) : [...value, sport]);
    };

    if (mode === 'compact') {
        return (
            <CompactSportSelect
                value={value}
                sports={sports}
                toggle={toggle}
                emptyLabel={emptyLabel ?? translate('sportPicker.select')}
                unitLabel={unitLabel ?? translate('sportPicker.unit')}
            />
        );
    }

    return <InlineSportSelect value={value} sports={sports} toggle={toggle} isInvalid={isInvalid} />;
}

function InlineSportSelect({
    value,
    sports,
    toggle,
    isInvalid,
}: {
    value: SportType[];
    sports: SportType[];
    toggle: (sport: SportType) => void;
    isInvalid: boolean;
}) {
    const border = useSemanticColor('border');
    const textSecondary = useSemanticColor('textSecondary');
    const cardBg = useSemanticColor('cardBg');

    return (
        <Wrap
            spacing={2}
            p={isInvalid ? 2 : 0}
            borderWidth={isInvalid ? '1px' : 0}
            borderColor="red.500"
            borderRadius="md"
        >
            {sports.map(sport => {
                const selected = value.includes(sport);
                return (
                    <WrapItem key={sport}>
                        <Tag
                            as="button"
                            type="button"
                            role="checkbox"
                            aria-checked={selected}
                            onClick={() => toggle(sport)}
                            size="lg"
                            borderRadius="full"
                            px={3}
                            minH="36px"
                            cursor="pointer"
                            fontWeight="600"
                            fontSize="sm"
                            bg={selected ? 'brand.500' : cardBg}
                            color={selected ? 'white' : textSecondary}
                            borderWidth="1px"
                            borderColor={selected ? 'brand.500' : border}
                            transition="background 0.15s, border-color 0.15s, color 0.15s"
                            _hover={{
                                borderColor: 'brand.500',
                                color: selected ? 'white' : 'brand.500',
                            }}
                        >
                            {selected && <CheckIcon boxSize={2.5} mr={1.5} />}
                            {sport}
                        </Tag>
                    </WrapItem>
                );
            })}
        </Wrap>
    );
}

function CompactSportSelect({
    value,
    sports,
    toggle,
    emptyLabel,
    unitLabel,
}: {
    value: SportType[];
    sports: SportType[];
    toggle: (sport: SportType) => void;
    emptyLabel: string;
    unitLabel: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const textMuted = useSemanticColor('textMuted');

    const label =
        value.length === 0
            ? emptyLabel
            : value.length > 3
                ? `${value.length} ${unitLabel}`
                : value.join(', ');

    const filtered = useMemo(
        () => sports.filter(s => s.toLowerCase().includes(query.trim().toLowerCase())),
        [sports, query],
    );

    return (
        <Popover
            isOpen={isOpen}
            onClose={() => {
                setIsOpen(false);
                setQuery('');
            }}
        >
            <PopoverTrigger>
                <Button
                    onClick={() => setIsOpen(true)}
                    variant="outline"
                    size="sm"
                    rightIcon={<ChevronDownIcon />}
                    fontWeight="600"
                    fontSize="sm"
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader fontWeight="600">{emptyLabel}</PopoverHeader>
                    <Box px={3} pt={3}>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color={textMuted} boxSize={3} />
                            </InputLeftElement>
                            <Input
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder={translate('sportPicker.search')}
                                autoFocus
                                borderRadius="md"
                            />
                        </InputGroup>
                    </Box>
                    <PopoverBody maxH="280px" overflowY="auto">
                        <VStack align="start" spacing={2}>
                            {filtered.map(sport => (
                                <Checkbox
                                    key={sport}
                                    isChecked={value.includes(sport)}
                                    onChange={() => toggle(sport)}
                                >
                                    {sport}
                                </Checkbox>
                            ))}
                            {filtered.length === 0 && (
                                <Text fontSize="sm" color={textMuted}>
                                    {translate('sportPicker.no_results')}
                                </Text>
                            )}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}
